import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import useAssignmentStore from '../stores/useAssignmentStore';
import { useToast } from '../components/ToastContainer';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';

const AssignmentPage = () => {
  const { courseId, lessonId, assignmentId } = useParams();
  const { user } = useAuthStore();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    course,
    fetchCourse,
    isLoading: isCourseLoading,
    error: courseError
  } = useCourseStore();
  const {
    assignment,
    submission,
    fetchAssignmentForStudent,
    fetchMySubmission,
    submitAssignment,
    isLoading: isAssignmentLoading,
    error: assignmentError
  } = useAssignmentStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    submissionType: 'file',
    file: null,
    textContent: '',
    link: ''
  });

  useEffect(() => {
    if (assignmentId && courseId) {
      fetchCourse(courseId);
      fetchAssignmentForStudent(assignmentId);
      fetchMySubmission(assignmentId);
    }
  }, [courseId, assignmentId, fetchCourse, fetchAssignmentForStudent, fetchMySubmission]);

  useEffect(() => {
    if (submission) {
      setSubmissionData({
        submissionType: submission.submissionType || 'file',
        file: null,
        textContent: submission.textContent || '',
        link: submission.link || ''
      });
    }
  }, [submission]);

  const canSubmit = () => {
    if (!assignment) return false;
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    return assignment.allowLateSubmissions || now <= dueDate;
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setSubmissionData((prev) => ({ ...prev, file: files[0] }));
    } else {
      setSubmissionData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) {
      toast.error('Submission deadline has passed');
      return;
    }

    const { submissionType, file, textContent, link } = submissionData;
    if (
      (submissionType === 'file' && !file) ||
      (submissionType === 'text' && !textContent.trim()) ||
      (submissionType === 'link' && !link.trim())
    ) {
      toast.error('Please provide a valid submission');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('submissionType', submissionType);
      formData.append('lessonId', lessonId);

      if (submissionType === 'file' && file) {
        formData.append('file', file);
      } else if (submissionType === 'text') {
        formData.append('textContent', textContent);
      } else if (submissionType === 'link') {
        formData.append('link', link);
      }

      const result = await submitAssignment(assignmentId, formData);

      if (result?.green) {
        toast.success(submission ? 'Submission updated!' : 'Assignment submitted!');
        await fetchMySubmission(assignmentId);
        navigate(`/student/learning/${courseId}`);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCourseLoading || isAssignmentLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (courseError || assignmentError || !course || !assignment) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Assignment not found</h2>
            <button onClick={() => navigate(-1)} className="text-primary-600 dark:text-primary-400 hover:underline">
              Go back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isPastDue = new Date() > new Date(assignment.dueDate);

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(`/student/learning/${courseId}`)}
            className="text-primary-600 dark:text-primary-400 hover:underline mb-6 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Course
          </button>

          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">{assignment.title}</h1>
              <p className="text-neutral-600 dark:text-neutral-400 mt-2">{assignment.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Due Date</p>
                <p
                  className={`text-lg font-medium ${isPastDue && !assignment.allowLateSubmissions ? 'text-red-600' : 'text-neutral-900'} dark:text-white`}
                >
                  {new Date(assignment.dueDate).toLocaleString()}
                  {isPastDue && !assignment.allowLateSubmissions && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded">
                      Overdue
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Points</p>
                <p className="text-lg font-medium text-neutral-900 dark:text-white">{assignment.points}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Allowed Submission Types</p>
                <p className="text-lg font-medium text-neutral-900 dark:text-white">{assignment.submissionTypes.join(', ')}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Status</p>
                <p className="text-lg font-medium text-neutral-900 dark:text-white">
                  {submission ? 'Submitted' : 'Not Submitted'}
                </p>
              </div>
            </div>

            {assignment.instructions?.length > 0 && (
              <div className="mb-8 p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                <h3 className="font-medium text-neutral-900 dark:text-white mb-2">Instructions</h3>
                <ul className="list-disc pl-5 text-neutral-600 dark:text-neutral-400 space-y-1">
                  {assignment.instructions.map((instr, i) => (
                    <li key={i}>{instr}</li>
                  ))}
                </ul>
              </div>
            )}

            {submission && (
              <div className="mb-8 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <h3 className="font-medium text-green-700 dark:text-green-300 mb-2">Your Submission</h3>
                {submission.submissionType === 'file' && submission.fileUrl && (
                  <a
                    href={submission.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Download Submitted File
                  </a>
                )}
                {submission.submissionType === 'text' && (
                  <p className="text-neutral-600 dark:text-neutral-400 whitespace-pre-wrap">{submission.textContent}</p>
                )}
                {submission.submissionType === 'link' && (
                  <a
                    href={submission.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {submission.link}
                  </a>
                )}
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
                  Submitted on: {new Date(submission.submittedAt).toLocaleString()}
                </p>
                {submission.gradedAt && (
                  <div className="mt-3 p-3 bg-white dark:bg-neutral-800 rounded border border-neutral-200 dark:border-neutral-700">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white">
                      Grade: <span className="text-green-600">{submission.grade}/{assignment.points}</span>
                    </p>
                    {submission.feedback && (
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">Feedback: {submission.feedback}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {!submission?.gradedAt && canSubmit() && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Submission Type
                  </label>
                  <select
                    name="submissionType"
                    value={submissionData.submissionType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {assignment.submissionTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {submissionData.submissionType === 'file' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Upload File
                    </label>
                    <input
                      type="file"
                      name="file"
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {submissionData.submissionType === 'text' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Text Submission
                    </label>
                    <textarea
                      name="textContent"
                      value={submissionData.textContent}
                      onChange={handleInputChange}
                      rows={6}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter your submission here..."
                    />
                  </div>
                )}

                {submissionData.submissionType === 'link' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Link Submission
                    </label>
                    <input
                      type="url"
                      name="link"
                      value={submissionData.link}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="https://example.com"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {isSubmitting ? 'Submitting...' : submission ? 'Update Submission' : 'Submit Assignment'}
                </button>
              </form>
            )}

            {!canSubmit() && !submission?.gradedAt && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">
                  Submission deadline has passed. Late submissions are not allowed for this assignment.
                </p>
              </div>
            )}

            {submission?.gradedAt && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300">
                  This assignment has been graded. Resubmission is not allowed.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AssignmentPage;